import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Coffee,
  Instagram,
  Description,
  Code,
  Storage,
  Web,
} from '@mui/icons-material';
import { XIcon, LinkedInIcon } from '../icons/SocialIcons'; 
import { useNavigate } from 'react-router-dom';
import CommunityChatModal from '../Chat/ChatModal'; 
import Grid from '@mui/material/Grid';

interface FooterProps {
  isDark?: boolean;
}

const Footer: React.FC<FooterProps> = ({ isDark: propIsDark }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = propIsDark ?? theme.palette.mode === 'dark';
  
  // State for chat modal
  const [chatOpen, setChatOpen] = useState(false);
  
  // --- LOKI THEME PALETTE ---
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';
  
  // Text Colors
  const textPrimary = isDark ? '#FFFFFF' : '#051814';
  const textSecondary = isDark ? '#888888' : '#4F5660';
  const accentColor = goldColor;

  // Glass Styling
  const glassEffect = {
    backdropFilter: 'blur(20px)',
    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.6)',
    border: `1px solid ${isDark ? alpha(goldColor, 0.2) : alpha(deepGreen, 0.1)}`,
    boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.05)',
  };

  const discordFont = {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  };

  // Handler for City Guides - scroll to StateBoxesSection
  const handleCityGuidesClick = () => {
    // Check if we're already on the home page
    const isHomePage = window.location.pathname === '/';
    
    if (!isHomePage) {
      // Navigate to home page first
      navigate('/');
      
      // Wait for navigation to complete before scrolling
      setTimeout(() => {
        scrollToSection('state-boxes-section');
      }, 300);
    } else {
      // Already on home page, just scroll
      scrollToSection('state-boxes-section');
    }
  };

  // Handler for Meetups, Skill Sharing, Networking - scroll to ComingSoonSection
  const handleComingSoonClick = () => {
    // Check if we're already on the home page
    const isHomePage = window.location.pathname === '/';
    
    if (!isHomePage) {
      // Navigate to home page first
      navigate('/');
      
      // Wait for navigation to complete before scrolling
      setTimeout(() => {
        scrollToSection('coming-soon-section');
      }, 300);
    } else {
      // Already on home page, just scroll
      scrollToSection('coming-soon-section');
    }
  };

  const scrollToSection = (sectionId: string) => {
    // Use requestAnimationFrame for better timing
    requestAnimationFrame(() => {
      setTimeout(() => {
        const sectionElement = document.getElementById(sectionId);
        if (sectionElement) {
          sectionElement.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        } else {
          // If element not found immediately, try again after a bit
          setTimeout(() => {
            const retrySection = document.getElementById(sectionId);
            if (retrySection) {
              retrySection.scrollIntoView({ 
                behavior: 'smooth',
                block: 'start'
              });
            }
          }, 500);
        }
      }, 100);
    });
  };

  // Handler for Remote Workers Hub - open chat modal
  const handleRemoteWorkersHubClick = () => {
    setChatOpen(true);
  };

  // Handler for Support links - redirect to external portfolio (keep some external)
  const handleExternalLinkClick = () => {
    window.open('https://kaiftokare19.github.io/Portfolio-Website', '_blank', 'noopener,noreferrer');
  };

  // Handler for Documentation - navigate to /docs route
  const handleDocumentationClick = () => {
    navigate('/docs');
  };

  // Handler for specific documentation sections
  const handleDocsSectionClick = (tab: string) => {
    navigate('/docs', { state: { defaultTab: tab } });
  };

  return (
    <>
      {/* Chat Modal */}
      <CommunityChatModal 
        open={chatOpen} 
        onClose={() => setChatOpen(false)} 
      />

      <Box
        component="footer"
        sx={{
          pt: 8,
          bgcolor: isDark ? '#000000' : '#FAFAFA',
          borderTop: `1px solid ${isDark ? '#1A1A1A' : '#E0E0E0'}`,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          {/* TOP SECTION: Links & Info */}
          <Grid container spacing={4} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box
                sx={{
                  p: 3,
                  borderRadius: '24px',
                  ...glassEffect,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    mb: 2,
                    background: `linear-gradient(45deg, ${goldColor} 30%, #44A08D 90%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    ...discordFont,
                  }}
                >
                  RemoteWork Cafe Connect
                </Typography>
                <Typography
                  sx={{
                    color: textSecondary,
                    mb: 3,
                    lineHeight: 1.7,
                    fontSize: '0.95rem'
                  }}
                >
                  The platform connecting remote workers with perfect cafes for collaboration. 
                  Find your ideal workspace, meet like-minded professionals, and boost productivity.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {[
                    {
                      Icon: Instagram,
                      href: 'https://instagram.com/WFHCafee',
                      target: '_blank',
                      rel: 'noopener noreferrer'
                    },
                    {
                      Icon: XIcon,
                      href: 'https://x.com/WFHCafee',
                      target: '_blank',
                      rel: 'noopener noreferrer'
                    }
                  ].map((social, index) => (
                    <IconButton
                      key={index}
                      component="a"
                      href={social.href}
                      target={social.target}
                      rel={social.rel}
                      sx={{
                        color: textSecondary,
                        border: `1px solid ${alpha(textSecondary, 0.2)}`,
                        backdropFilter: 'blur(10px)',
                        '&:hover': {
                          color: accentColor,
                          borderColor: accentColor,
                          transform: 'translateY(-2px)',
                          bgcolor: alpha(accentColor, 0.1)
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      <social.Icon />
                    </IconButton>
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* Explore, Community, Documentation - With video on desktop */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Grid container spacing={0} sx={{ height: '100%' }}>
                {/* Explore Section */}
                <Grid size={{ xs: 12, md: 3 }}>
                  <Box
                    sx={{
                      height: '100%',
                      px: 3,
                      py: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <Typography
                      sx={{
                        mb: 3,
                        fontSize: '1rem',
                        color: isDark ? '#fff' : deepGreen,
                        ...discordFont,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}
                    >
                      Explore
                    </Typography>
                    <Stack spacing={1.5}>
                      <Button
                        onClick={() => navigate('/explore')}
                        sx={{
                          justifyContent: 'flex-start',
                          color: textSecondary,
                          textTransform: 'none',
                          p: 0,
                          minWidth: 'auto',
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          '&:hover': {
                            color: accentColor,
                            bgcolor: 'transparent',
                            transform: 'translateX(4px)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        Find Cafes
                      </Button>
                      <Button
                        onClick={() => navigate('/community')}
                        sx={{
                          justifyContent: 'flex-start',
                          color: textSecondary,
                          textTransform: 'none',
                          p: 0,
                          minWidth: 'auto',
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          '&:hover': {
                            color: accentColor,
                            bgcolor: 'transparent',
                            transform: 'translateX(4px)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        Community Events
                      </Button>
                      <Button
                        onClick={() => navigate('/previously-viewed')}
                        sx={{
                          justifyContent: 'flex-start',
                          color: textSecondary,
                          textTransform: 'none',
                          p: 0,
                          minWidth: 'auto',
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          '&:hover': {
                            color: accentColor,
                            bgcolor: 'transparent',
                            transform: 'translateX(4px)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        Workspace Reviews
                      </Button>
                      <Button
                        onClick={handleCityGuidesClick}
                        sx={{
                          justifyContent: 'flex-start',
                          color: textSecondary,
                          textTransform: 'none',
                          p: 0,
                          minWidth: 'auto',
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          '&:hover': {
                            color: accentColor,
                            bgcolor: 'transparent',
                            transform: 'translateX(4px)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        City Guides
                      </Button>
                    </Stack>
                  </Box>
                </Grid>

                {/* Community Section */}
                <Grid size={{ xs: 12, md: 3 }}>
                  <Box
                    sx={{
                      height: '100%',
                      px: 3,
                      py: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <Typography
                      sx={{
                        mb: 3,
                        fontSize: '1rem',
                        color: isDark ? '#fff' : deepGreen,
                        ...discordFont,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}
                    >
                      Community
                    </Typography>
                    <Stack spacing={1.5}>
                      {/* Remote Workers Hub - opens chat modal */}
                      <Button
                        onClick={handleRemoteWorkersHubClick}
                        sx={{
                          justifyContent: 'flex-start',
                          color: textSecondary,
                          textTransform: 'none',
                          p: 0,
                          minWidth: 'auto',
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          '&:hover': {
                            color: accentColor,
                            bgcolor: 'transparent',
                            transform: 'translateX(4px)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        Networking Hub
                      </Button>
                      
                      {/* Meetups, Skill Sharing, Networking - scroll to ComingSoonSection */}
                      <Button
                        onClick={handleComingSoonClick}
                        sx={{
                          justifyContent: 'flex-start',
                          color: textSecondary,
                          textTransform: 'none',
                          p: 0,
                          minWidth: 'auto',
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          '&:hover': {
                            color: accentColor,
                            bgcolor: 'transparent',
                            transform: 'translateX(4px)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        Meetups
                      </Button>
                      
                      <Button
                        onClick={handleComingSoonClick}
                        sx={{
                          justifyContent: 'flex-start',
                          color: textSecondary,
                          textTransform: 'none',
                          p: 0,
                          minWidth: 'auto',
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          '&:hover': {
                            color: accentColor,
                            bgcolor: 'transparent',
                            transform: 'translateX(4px)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        Skill Sharing
                      </Button>
                      
                      <Button
                        onClick={handleComingSoonClick}
                        sx={{
                          justifyContent: 'flex-start',
                          color: textSecondary,
                          textTransform: 'none',
                          p: 0,
                          minWidth: 'auto',
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          '&:hover': {
                            color: accentColor,
                            bgcolor: 'transparent',
                            transform: 'translateX(4px)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        Collaboration
                      </Button>
                    </Stack>
                  </Box>
                </Grid>

                {/* Documentation Section - All links go to /docs with specific tabs */}
                <Grid size={{ xs: 12, md: 3 }}>
                  <Box
                    sx={{
                      height: '100%',
                      px: 3,
                      py: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                    }}
                  >
                    <Typography
                      sx={{
                        mb: 3,
                        fontSize: '1rem',
                        color: isDark ? '#fff' : deepGreen,
                        ...discordFont,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}
                    >
                      Documentation
                    </Typography>
                    <Stack spacing={1.5}>
                      {/* Main Documentation Entry */}
                      <Button
                        onClick={() => handleDocsSectionClick('frontend-overview')}
                        sx={{
                          justifyContent: 'flex-start',
                          color: textSecondary,
                          textTransform: 'none',
                          p: 0,
                          minWidth: 'auto',
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          '&:hover': {
                            color: accentColor,
                            bgcolor: 'transparent',
                            transform: 'translateX(4px)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        Overview
                      </Button>
                      
                      {/* Frontend Sections */}
                      <Button
                        onClick={() => handleDocsSectionClick('frontend-tech-stack')}
                        sx={{
                          justifyContent: 'flex-start',
                          color: textSecondary,
                          textTransform: 'none',
                          p: 0,
                          minWidth: 'auto',
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          '&:hover': {
                            color: accentColor,
                            bgcolor: 'transparent',
                            transform: 'translateX(4px)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        Tech Stack
                      </Button>
                      
                      <Button
                        onClick={() => handleDocsSectionClick('frontend-installation')}
                        sx={{
                          justifyContent: 'flex-start',
                          color: textSecondary,
                          textTransform: 'none',
                          p: 0,
                          minWidth: 'auto',
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          '&:hover': {
                            color: accentColor,
                            bgcolor: 'transparent',
                            transform: 'translateX(4px)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        Installation
                      </Button>
                      
                      <Button
                        onClick={() => handleDocsSectionClick('backend-api-endpoints')}
                        sx={{
                          justifyContent: 'flex-start',
                          color: textSecondary,
                          textTransform: 'none',
                          p: 0,
                          minWidth: 'auto',
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          '&:hover': {
                            color: accentColor,
                            bgcolor: 'transparent',
                            transform: 'translateX(4px)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        API Reference
                      </Button>

                      {/* Quick Links to specific sections */}
                      <Button
                        onClick={() => handleDocsSectionClick('frontend-security')}
                        sx={{
                          justifyContent: 'flex-start',
                          color: textSecondary,
                          textTransform: 'none',
                          p: 0,
                          minWidth: 'auto',
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          '&:hover': {
                            color: accentColor,
                            bgcolor: 'transparent',
                            transform: 'translateX(4px)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        Security Guide
                      </Button>
                    </Stack>
                  </Box>
                </Grid>

                {/* Video & Quick Links Section - Desktop Only */}
                <Grid size={{ xs: 12, md: 3 }}>
                  <Box
                    sx={{
                      height: '100%',
                      px: 3,
                      py: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                    }}
                  >
                    {/* Video Container - Simple and clean */}
                    <Box
                      onClick={handleExternalLinkClick}
                      sx={{
                        position: 'relative',
                        width: '100%',
                        height: { xs: '160px', md: '200px' },
                        borderRadius: '16px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.1)}`,
                      }}
                    >
                      {/* Video */}
                      <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      >
                        <source src="https://salmon-selective-jackal-307.mypinata.cloud/ipfs/bafybeidlz33xeepvtntoii7sifgp7jey3zyl5kooewth62ndcgovv66biy" type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </Box>
                    
                    {/* Quick Documentation Links */}
                    <Typography
                      sx={{
                        mt: 2,
                        mb: 1,
                        fontSize: '0.9rem',
                        color: isDark ? '#fff' : deepGreen,
                        ...discordFont,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}
                    >
                      Quick Docs
                    </Typography>
                    
                    <Stack spacing={1} direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleDocsSectionClick('frontend-overview')}
                        sx={{
                          color: textSecondary,
                          '&:hover': {
                            color: accentColor,
                            bgcolor: alpha(accentColor, 0.1),
                          },
                        }}
                      >
                        <Description fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDocsSectionClick('frontend-tech-stack')}
                        sx={{
                          color: textSecondary,
                          '&:hover': {
                            color: accentColor,
                            bgcolor: alpha(accentColor, 0.1),
                          },
                        }}
                      >
                        <Code fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDocsSectionClick('backend-structure')}
                        sx={{
                          color: textSecondary,
                          '&:hover': {
                            color: accentColor,
                            bgcolor: alpha(accentColor, 0.1),
                          },
                        }}
                      >
                        <Storage fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDocsSectionClick('backend-api-endpoints')}
                        sx={{
                          color: textSecondary,
                          '&:hover': {
                            color: accentColor,
                            bgcolor: alpha(accentColor, 0.1),
                          },
                        }}
                      >
                        <Web fontSize="small" />
                      </IconButton>
                    </Stack>
                    
                    {/* Full Documentation Button */}
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Description />}
                      onClick={handleDocumentationClick}
                      sx={{
                        mt: 2,
                        color: accentColor,
                        borderColor: alpha(accentColor, 0.3),
                        '&:hover': {
                          borderColor: accentColor,
                          bgcolor: alpha(accentColor, 0.1),
                        },
                      }}
                    >
                      Full Documentation
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>

        {/* MIDDLE SECTION: MASSIVE TEXT */}
        <Box
          sx={{
            width: '100%',
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 4,
            opacity: 0.8
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '18vw', md: '18vw' },
              fontWeight: 900,
              lineHeight: 0.8,
              fontFamily: '"Inter", sans-serif',
              textAlign: 'center',
              color: 'transparent',
              WebkitTextStroke: isDark 
                ? `2px ${alpha(goldColor, 0.15)}` 
                : `2px ${alpha(deepGreen, 0.1)}`,
              background: isDark 
                ? `linear-gradient(to bottom, transparent 20%, ${alpha(deepGreen, 0.5)} 100%)`
                : `linear-gradient(to bottom, transparent 20%, ${alpha(deepGreen, 0.05)} 100%)`,
              WebkitBackgroundClip: 'text',
              textTransform: 'uppercase',
              letterSpacing: '-0.05em',
              whiteSpace: 'nowrap',
              userSelect: 'none',
            }}
          >
            WFHCafe
          </Typography>
        </Box>

        {/* BOTTOM SECTION: COPYRIGHT LINE */}
        <Box 
          sx={{ 
            borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            bgcolor: isDark ? '#050505' : '#F5F5F5',
            py: 3,
            position: 'relative',
            zIndex: 3
          }}
        >
          <Container maxWidth="xl">
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Typography
                sx={{
                  color: textSecondary,
                  fontSize: '0.85rem',
                  opacity: 0.8,
                }}
              >
                © 2026 WFCafe - You're Office is Where The Coffee is !! 
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {/* Quick Documentation Link */}
                <Button
                  variant="text"
                  size="small"
                  onClick={handleDocumentationClick}
                  sx={{
                    color: textSecondary,
                    fontSize: '0.85rem',
                    textTransform: 'none',
                    minWidth: 'auto',
                    p: 0,
                    '&:hover': {
                      color: accentColor,
                      bgcolor: 'transparent',
                    },
                  }}
                >
                  Documentation
                </Button>
                <Typography
                  sx={{
                    color: textSecondary,
                    fontSize: '0.85rem',
                    opacity: 0.8,
                  }}
                >
                  All Rights Reserved @kaiftokare
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default Footer;